// src/pages/AccuracyInsights.js
import React, { useEffect, useState } from "react";
import "./AccuracyInsights.css";
import { auth, db } from "../services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import API_BASE from "../api/apiBase";

/* ---------------- HELPERS ---------------- */
function parseFeetInches(str) {
  if (!str) return 0;
  const match = str.match(/(\d+)'[\s]*(\d+)?/);
  if (!match) return 0;
  const feet = parseInt(match[1], 10);
  const inches = match[2] ? parseInt(match[2], 10) : 0;
  return feet * 12 + inches;
}

function formatDelta(actual, ai) {
  const diff = ai - actual;
  const sign = diff > 0 ? "+" : "";
  return `${sign}${diff} inches`;
}

function getResultLabel(delta) {
  const abs = Math.abs(delta);
  if (abs <= 2) return "Excellent accuracy ✅";
  if (abs <= 6) return "Within acceptable range ⚠️";
  return "Significant deviation ⚠️";
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

/* ---------------- COMPONENT ---------------- */
export default function AccuracyInsights() {
  const [user, setUser] = useState(null);
  const [examples, setExamples] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, []);

  async function loadExamples() {
    try {
      const snap = await getDocs(collection(db, "accuracyInsights"));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setExamples(data);
    } catch (err) {
      console.warn("Load failed:", err);
    }
  }

  useEffect(() => {
    loadExamples();
  }, []);

  async function handleDelete(id, title) {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${title}"? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(id);
    try {
      await deleteDoc(doc(db, "accuracyInsights", id));
      setExamples((prev) => prev.filter((ex) => ex.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete. Please try again.");
    } finally {
      setDeletingId(null);
    }
  }

  async function uploadImage(file) {
    const base64 = await toBase64(file);
    const res = await fetch(`${API_BASE}/api/upload-surface`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageBase64: base64 }),
    });
    if (!res.ok) {
      const text = await res.text();
      console.error("Upload error:", text);
      throw new Error("Upload failed");
    }
    const data = await res.json();
    if (!data.url) throw new Error("No URL returned");
    return data.url;
  }

  async function handleSave() {
    const titleEl = document.getElementById("title");
    const beforeImgEl = document.getElementById("beforeImg");
    const afterImgEl = document.getElementById("afterImg");
    const bwEl = document.getElementById("bw");
    const baiwEl = document.getElementById("baiw");
    const blEl = document.getElementById("bl");
    const bailEl = document.getElementById("bail");
    const awEl = document.getElementById("aw");
    const aaiwEl = document.getElementById("aaiw");
    const alEl = document.getElementById("al");
    const aailEl = document.getElementById("aail");
    const obsEl = document.getElementById("obs");
    const takeEl = document.getElementById("take");

    const beforeFile = beforeImgEl?.files?.[0];
    const afterFile = afterImgEl?.files?.[0];

    if (!beforeFile) {
      alert("Before image required");
      return;
    }

    setUploading(true);
    try {
      const beforeUrl = await uploadImage(beforeFile);
      const afterUrl = afterFile ? await uploadImage(afterFile) : null;

      const payload = {
        title: titleEl?.value || "",
        beforeImage: beforeUrl,
        afterImage: afterUrl,
        before: {
          actualWidth: bwEl?.value || "",
          aiWidth: baiwEl?.value || "",
          actualLength: blEl?.value || "",
          aiLength: bailEl?.value || "",
        },
        after: afterUrl
          ? {
              actualWidth: awEl?.value || "",
              aiWidth: aaiwEl?.value || "",
              actualLength: alEl?.value || "",
              aiLength: aailEl?.value || "",
            }
          : null,
        observation: obsEl?.value || "",
        takeaway: takeEl?.value || "",
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "accuracyInsights"), payload);
      setShowUpload(false);
      await loadExamples();
    } catch (err) {
      console.error(err);
      alert("Save failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <div className="ai-page">
        <div className="ai-page-header">
          <div>
            <h1 className="ai-page-title">Accuracy Insights</h1>
            <p className="ai-page-sub">
              Real examples comparing AI measurements to actual dimensions.
              Learn what impacts accuracy and how to get the best results.
            </p>
          </div>
          {user && (
            <button className="ai-add-btn" onClick={() => setShowUpload(true)}>
              + Add Example
            </button>
          )}
        </div>

        <h2 className="ai-section-title">Real-World Examples</h2>

        {examples.length === 0 && (
          <p style={{ color: "#6b7280", marginTop: 16 }}>
            No examples yet. Add one to get started.
          </p>
        )}

        {examples.map((ex) => {
          const beforeDelta =
            parseFeetInches(ex.before?.aiWidth) -
            parseFeetInches(ex.before?.actualWidth);

          return (
            <div key={ex.id} className="ai-card">
              {/* Card header with title + delete button */}
              <div className="ai-card-header">
                <h3 className="ai-card-title">{ex.title}</h3>
                {user && (
                  <button
                    className="ai-delete-btn"
                    onClick={() => handleDelete(ex.id, ex.title)}
                    disabled={deletingId === ex.id}
                    title="Delete this example"
                  >
                    {deletingId === ex.id ? "Deleting..." : "Delete"}
                  </button>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 20,
                  flexWrap: "nowrap",
                  alignItems: "flex-start",
                  justifyContent: ex.afterImage ? "space-between" : "center",
                }}
              >
                {/* BEFORE */}
                <div
                  style={{
                    flex: ex.afterImage ? 1 : "0 0 48%",
                    minWidth: 280,
                    maxWidth: ex.afterImage ? "none" : 520,
                  }}
                >
                  <b>Before</b>
                  {ex.beforeImage ? (
                    <img
                      src={ex.beforeImage}
                      alt="Before"
                      style={{
                        width: "100%",
                        height: 320,
                        objectFit: "cover",
                        borderRadius: 10,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        height: 320,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#999",
                        border: "1px dashed #ccc",
                        borderRadius: 10,
                      }}
                    >
                      No image uploaded
                    </div>
                  )}

                  <div style={{ marginTop: 10 }}>
                    <div>
                      Width:
                      <span style={{ fontWeight: 600, marginLeft: 6 }}>
                        Actual {ex.before?.actualWidth}
                      </span>
                      {" | "}
                      <span style={{ fontWeight: 600 }}>
                        AI {ex.before?.aiWidth}
                      </span>
                      <span style={{ color: "#1b365d", fontWeight: 700 }}>
                        {" "}
                        (
                        {formatDelta(
                          parseFeetInches(ex.before?.actualWidth),
                          parseFeetInches(ex.before?.aiWidth)
                        )}
                        )
                      </span>
                    </div>

                    <div>
                      Length:
                      <span style={{ fontWeight: 600, marginLeft: 6 }}>
                        Actual {ex.before?.actualLength}
                      </span>
                      {" | "}
                      <span style={{ fontWeight: 600 }}>
                        AI {ex.before?.aiLength}
                      </span>
                      <span style={{ color: "#1b365d", fontWeight: 700 }}>
                        {" "}
                        (
                        {formatDelta(
                          parseFeetInches(ex.before?.actualLength),
                          parseFeetInches(ex.before?.aiLength)
                        )}
                        )
                      </span>
                    </div>

                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      Result: {getResultLabel(beforeDelta)}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <b>Observation:</b> {ex.observation}
                    </div>
                    <div>
                      <b>Takeaway:</b> {ex.takeaway}
                    </div>
                  </div>
                </div>

                {/* AFTER */}
                {ex.afterImage && (
                  <div style={{ flex: 1, minWidth: 280 }}>
                    <b>After</b>
                    <img
                      src={ex.afterImage}
                      alt="After"
                      style={{
                        width: "100%",
                        height: 320,
                        objectFit: "cover",
                        borderRadius: 10,
                      }}
                    />
                    {ex.after && (
                      <div style={{ marginTop: 10 }}>
                        <div>
                          Width:
                          <span style={{ fontWeight: 600, marginLeft: 6 }}>
                            Actual {ex.after?.actualWidth}
                          </span>
                          {" | "}
                          <span style={{ fontWeight: 600 }}>
                            AI {ex.after?.aiWidth}
                          </span>
                          <span style={{ color: "#1b365d", fontWeight: 700 }}>
                            {" "}
                            (
                            {formatDelta(
                              parseFeetInches(ex.after?.actualWidth),
                              parseFeetInches(ex.after?.aiWidth)
                            )}
                            )
                          </span>
                        </div>
                        <div>
                          Length:
                          <span style={{ fontWeight: 600, marginLeft: 6 }}>
                            Actual {ex.after?.actualLength}
                          </span>
                          {" | "}
                          <span style={{ fontWeight: 600 }}>
                            AI {ex.after?.aiLength}
                          </span>
                          <span style={{ color: "#1b365d", fontWeight: 700 }}>
                            {" "}
                            (
                            {formatDelta(
                              parseFeetInches(ex.after?.actualLength),
                              parseFeetInches(ex.after?.aiLength)
                            )}
                            )
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ADD EXAMPLE MODAL */}
      {showUpload && (
        <div className="ai-modal-backdrop">
          <div className="ai-modal">
            <h2>Add Example</h2>

            <input id="title" placeholder="Title" className="ai-modal-input" />

            <b>Before Image (required)</b>
            <input id="beforeImg" type="file" className="ai-modal-input" />

            <b>After Image (optional)</b>
            <input id="afterImg" type="file" className="ai-modal-input" />

            <h4>Before</h4>
            <input id="bw" placeholder={`Actual Width (5' 7")`} className="ai-modal-input" />
            <input id="baiw" placeholder="AI Width" className="ai-modal-input" />
            <input id="bl" placeholder="Actual Length" className="ai-modal-input" />
            <input id="bail" placeholder="AI Length" className="ai-modal-input" />

            <h4>After (optional)</h4>
            <input id="aw" placeholder="Actual Width" className="ai-modal-input" />
            <input id="aaiw" placeholder="AI Width" className="ai-modal-input" />
            <input id="al" placeholder="Actual Length" className="ai-modal-input" />
            <input id="aail" placeholder="AI Length" className="ai-modal-input" />

            <textarea id="obs" placeholder="Observation" className="ai-modal-textarea" />
            <textarea id="take" placeholder="Takeaway" className="ai-modal-textarea" />

            <div className="ai-modal-footer">
              <button
                className="ai-modal-cancel"
                onClick={() => setShowUpload(false)}
              >
                Cancel
              </button>
              <button
                className="ai-modal-save"
                onClick={handleSave}
                disabled={uploading}
              >
                {uploading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
